import { useCurrentUserRoles } from '@/hooks/useCurrentUserRoles';

interface RequireRoleProps {
  roles: string[];
  children: React.ReactNode;
}

/**
 * Role gate for a route.
 *
 * A user who lacks the role is shown WHY. The previous behaviour was a silent
 * `<Navigate to="/dashboard">`, which from the user's side is indistinguishable
 * from a broken link: the page simply never opens, nothing is logged, and the
 * server sees no error because the request is never made. That is the single
 * most confusing way to deny access.
 *
 * This is the "Awaiting role assignment" surface IMPLEMENTATION_PLAN T5 called
 * for. It matters more under Cornerstone SSO than it did locally: roles now come
 * from the token's `app_roles`, so a user whose membership has no ServiceReady
 * app-role assigned signs in perfectly and then cannot reach anything.
 */
export function RequireRole({ roles, children }: RequireRoleProps) {
  const { roles: userRoles, isSuperuser, loading } = useCurrentUserRoles();

  if (loading) {
    return <div className="min-h-screen bg-warm-bg" />;
  }

  const hasRole = isSuperuser || userRoles.some((r) => roles.includes(r));
  if (hasRole) return <>{children}</>;

  // Signed in but holding nothing: almost always an unassigned app role rather
  // than a genuine permission refusal, so say so specifically.
  const awaitingAssignment = userRoles.length === 0;

  return (
    <div className="min-h-screen bg-warm-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-xl border border-gray-200 bg-white p-6 text-center">
        <h1 className="text-lg font-semibold text-gray-900">
          {awaitingAssignment ? 'Awaiting role assignment' : 'You don’t have access to this page'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {awaitingAssignment ? (
            <>
              Your account is set up, but no ServiceReady role has been assigned to you yet.
              An administrator assigns roles in Cornerstone; once that is done, sign out and
              back in to pick it up.
            </>
          ) : (
            <>
              This page needs {roles.join(' or ')}. Your account has{' '}
              {userRoles.join(', ')}.
            </>
          )}
        </p>
        <a
          href="/dashboard"
          className="mt-6 inline-block rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white"
        >
          Back to dashboard
        </a>
      </div>
    </div>
  );
}

export default RequireRole;
